// billingEntitlements.js
// Single frontend entitlement helper — mirrors the backend's getEntitlements().
// All billing-gating logic in the app routes through this one function.
// Do not duplicate this logic in components.

/**
 * @param {string} accessState — one of:
 *   not_provisioned | full_trial_access | full_paid_access |
 *   billing_action_required | blocked_trial_expired | blocked_canceled |
 *   blocked_billing_issue | billing_unavailable
 */
export function getEntitlements(accessState) {
  const isBlocked =
    accessState === 'blocked_trial_expired' ||
    accessState === 'blocked_canceled' ||
    accessState === 'blocked_billing_issue' ||
    accessState === 'billing_unavailable';

  return {
    // Allows access to premium app screens (budgets, transactions, etc.)
    canAccessPremium:
      accessState === 'full_trial_access' ||
      accessState === 'full_paid_access' ||
      accessState === 'billing_action_required' ||
      accessState === 'not_provisioned', // still onboarding — don't gate

    // Billing / settings always accessible so user can fix their situation
    canAccessBilling: true,

    // Whether a hard block screen should be shown
    isBlocked,

    // Whether we are in a blocked-trial-expired state specifically
    isTrialExpired: accessState === 'blocked_trial_expired',

    // Whether we are in a canceled/inactive state
    isCanceled: accessState === 'blocked_canceled',

    isBillingBlocked: accessState === 'blocked_billing_issue',

    isBillingUnavailable: accessState === 'billing_unavailable',

    // Whether a persistent billing warning should be surfaced (but not blocked)
    needsBillingAction: accessState === 'billing_action_required',

    isTrialing:
      accessState === 'full_trial_access',

    isPaid: accessState === 'full_paid_access',

    isProvisioned: accessState !== 'not_provisioned',
  };
}

/**
 * Compute how many full days remain until a given ISO date.
 * Returns null if date is absent.
 */
export function daysUntil(isoDate) {
  if (!isoDate) return null;
  const msLeft = new Date(isoDate).getTime() - Date.now();
  if (msLeft <= 0) return 0;
  return Math.ceil(msLeft / (1000 * 60 * 60 * 24));
}

/**
 * Format a date as "Jan 14" or "Jan 14 2025".
 * By default the year is included only when different from the current year.
 */
export function formatBillingDate(isoDate, options = {}) {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const includeYear = options.alwaysIncludeYear || d.getFullYear() !== now.getFullYear();
  const formatOptions = includeYear
    ? { month: 'short', day: 'numeric', year: 'numeric' }
    : { month: 'short', day: 'numeric' };
  const parts = new Intl.DateTimeFormat('en-US', formatOptions).formatToParts(d);
  const month = parts.find((part) => part.type === 'month')?.value || '';
  const day = parts.find((part) => part.type === 'day')?.value || '';
  const year = parts.find((part) => part.type === 'year')?.value || '';

  return includeYear ? `${month} ${day} ${year}`.trim() : `${month} ${day}`.trim();
}

/**
 * Format a price amount (in cents) with currency symbol.
 */
export function formatPrice(amountCents, currency = 'USD') {
  const amount = (amountCents || 0) / 100;
  return new Intl.NumberFormat(currency === 'CAD' ? 'en-CA' : 'en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}
